import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Lock, Network, Map, RefreshCw, Users, ArrowLeft } from 'lucide-react';
import { useInstance, useInstanceId } from '../../contexts/InstanceContext';
import { getViewByKey, isViewEnabled } from '../../config/systemsAnalysisViews';

/**
 * Shared shell for every systems-analysis view (DRY — never clone this per view).
 *
 * Responsibilities:
 *  - Resolve the current instance from ?kid=/?instance= + InstanceContext.
 *  - Gate on the per-instance feature flag for `viewKey`. If disabled (or no
 *    instance selected), render an explanatory state instead of the view.
 *  - Provide consistent header chrome (title, "located in", phase badge).
 *
 * Children receive nothing special — they read instance/flags via the usual
 * hooks. Keep view-specific logic in the child screen, layout/gating here.
 */

const ICONS = { Network, Map, RefreshCw, Users };

const Page = styled.div`
  min-height: var(--kuutio-vh, 100vh);
  color: #fff;
  padding: 32px clamp(16px, 4vw, 48px);
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
  max-width: 1100px;

  .icon-badge {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(33, 150, 243, 0.25), rgba(156, 39, 176, 0.25));
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  h1 {
    margin: 0 0 4px;
    font-size: clamp(1.4rem, 3vw, 2rem);
    font-weight: 700;
  }

  .sub {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.95rem;
    line-height: 1.4;
  }

  .meta {
    margin-top: 8px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 0.78rem;

    span {
      padding: 3px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.75);
    }
  }
`;

const Notice = styled.div`
  max-width: 640px;
  margin: 12vh auto 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);

  .lock {
    width: 56px;
    height: 56px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
  }

  h2 { margin: 0 0 10px; font-size: 1.3rem; }
  p { margin: 0 0 8px; color: rgba(255, 255, 255, 0.6); line-height: 1.5; }
  code {
    background: rgba(255, 255, 255, 0.08);
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.85rem;
  }
`;

const Scaffold = styled.div`
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 28px;
  max-width: 1100px;
  background: rgba(255, 255, 255, 0.02);
`;

export default function SystemsViewScaffold({ viewKey, children }) {
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);
  const { currentInstance } = useInstance();

  const view = getViewByKey(viewKey);
  if (!view) {
    return (
      <Page>
        <Notice>
          <h2>Tuntematon näkymä</h2>
          <p>Näkymää <code>{viewKey}</code> ei löydy rekisteristä.</p>
        </Notice>
      </Page>
    );
  }

  const Icon = ICONS[view.icon] || Network;

  // No instance in the URL/context yet — can't resolve flags.
  if (!instanceId) {
    return (
      <Page>
        <Notice>
          <div className="lock"><Lock size={26} /></div>
          <h2>{view.label}</h2>
          <p>Instanssia ei ole valittu.</p>
          <p>Avaa näkymä instanssin kanssa: <code>?kid=&lt;instanssin-id&gt;</code></p>
        </Notice>
      </Page>
    );
  }

  // Instance resolved but flag is off (or instance still loading).
  if (!isViewEnabled(currentInstance, viewKey)) {
    return (
      <Page>
        <Notice>
          <div className="lock"><Lock size={26} /></div>
          <h2>{view.label}</h2>
          <p>Tämä näkymä on poistettu käytöstä tässä instanssissa.</p>
          <p>Ota käyttöön: <code>Hallinta → Instanssin asetukset → Järjestelmäanalyysi</code></p>
        </Notice>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <div className="icon-badge"><Icon size={26} /></div>
        <div>
          <h1>{view.label}</h1>
          <div className="sub">{view.description}</div>
          <div className="meta">
            <span>Vaihe {view.phase}</span>
            <span>Sijainti: {view.locatedIn}</span>
          </div>
        </div>
      </Header>
      <Scaffold>{children}</Scaffold>
    </Page>
  );
}

/**
 * Small placeholder body for not-yet-implemented views. Keeps each scaffolded
 * screen tiny while clearly telegraphing what will live there.
 */
export function ComingSoonBody({ children }) {
  return (
    <div style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ArrowLeft size={16} style={{ opacity: 0.5 }} />
        <strong style={{ color: '#fff' }}>Rakenteilla</strong>
      </div>
      {children}
    </div>
  );
}